import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection, $isRangeSelection, $isRootOrShadowRoot,
  $getNodeByKey, $getRoot, $createTextNode,
  $createRangeSelection, $setSelection,
  CAN_REDO_COMMAND, CAN_UNDO_COMMAND, FORMAT_ELEMENT_COMMAND, FORMAT_TEXT_COMMAND,
  REDO_COMMAND, SELECTION_CHANGE_COMMAND, UNDO_COMMAND, COMMAND_PRIORITY_CRITICAL,
  INDENT_CONTENT_COMMAND, OUTDENT_CONTENT_COMMAND,
} from 'lexical';
import { $isTableSelection, $isTableCellNode } from '@lexical/table';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import {
  $isListNode, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND,
  ListNode, REMOVE_LIST_COMMAND, INSERT_CHECK_LIST_COMMAND,
} from '@lexical/list';
import { $isHeadingNode, $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
import { $setBlocksType } from '@lexical/selection';
import { $findMatchingParent, $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import { $isCodeNode } from '@lexical/code';
import { $patchStyleText, $getSelectionStyleValueForProperty } from '@lexical/selection';
import { $createParagraphNode } from 'lexical';
import { INSERT_HORIZONTAL_RULE_COMMAND } from '@lexical/react/LexicalHorizontalRuleNode';
import {
  IconBold, IconItalic, IconUnderline, IconStrikethrough,
  IconAlignLeft, IconAlignCenter, IconAlignRight, IconAlignJustified,
  IconList, IconListNumbers, IconArrowBackUp, IconArrowForwardUp,
  IconTable, IconPageBreak, IconIndentDecrease, IconIndentIncrease,
  IconCode, IconLink, IconSubscript, IconSuperscript, IconHighlight,
  IconMinus, IconChecklist, IconClearFormatting, IconPhoto,
  IconSettings, IconChevronDown, IconChevronUp, IconLineHeight,
  IconBorderOuter, IconCheck, IconRuler, IconPencil, IconBraces,
  IconLayoutColumns,
} from '@tabler/icons-react';
import { notifications } from '../../../../lib/notifications';
import { INSERT_PAGE_BREAK_COMMAND } from '../nodes/PageBreakNode';
import { INSERT_LAYOUT_COMMAND } from '../nodes/LayoutNode';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../ui/dialog';
import { Popover as RadixPopover, PopoverContent, PopoverTrigger } from '../../../ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../../../ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import styles from '../LexicalDocEditor.module.scss';

const COMMON_SIZES = [
  '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18',
  '19', '20', '24', '28', '32', '36', '40', '48', '56', '60', '72', '96',
];

const FONT_FAMILIES = [
  'Kalpurush', 'Noto Sans Bengali', 'Noto Serif Bengali', 'Hind Siliguri',
  'Baloo Da 2', 'Tiro Bangla', 'Anek Bangla', 'Mina', 'Galada', 'SolaimanLipi',
  'Arial', 'Arial Black', 'Comic Sans MS', 'Courier New',
  'Georgia', 'Impact', 'Lucida Console', 'Tahoma',
  'Times New Roman', 'Trebuchet MS', 'Verdana',
];

const DEFAULT_FONT_FAMILY = 'Kalpurush';

const LINE_HEIGHTS = [
  { value: '0.5',  label: '0.5' },
  { value: '1.0',  label: '1.0' },
  { value: '1.15', label: '1.15' },
  { value: '1.5',  label: '1.5' },
  { value: '2.0',  label: '2.0' },
  { value: '2.5',  label: '2.5' },
  { value: '3.0',  label: '3.0' },
];
const DEFAULT_LINE_HEIGHT = '1.5';

const MARGIN_UNITS = [
  { value: 'px',   label: 'px',   toPx: (v) => v,               fromPx: (v) => v,               step: 1,    decimals: 0 },
  { value: 'mm',   label: 'mm',   toPx: (v) => v * (96 / 25.4), fromPx: (v) => v * (25.4 / 96), step: 0.5,  decimals: 1 },
  { value: 'cm',   label: 'cm',   toPx: (v) => v * (96 / 2.54), fromPx: (v) => v * (2.54 / 96), step: 0.1,  decimals: 2 },
  { value: 'inch', label: 'inch', toPx: (v) => v * 96,          fromPx: (v) => v / 96,          step: 0.05, decimals: 2 },
];
const getUnit = (u) => MARGIN_UNITS.find(x => x.value === u) || MARGIN_UNITS[0];
const roundTo = (v, decimals) => {
  const m = Math.pow(10, decimals);
  return Math.round(v * m) / m;
};

const BLOCK_TYPES = [
  { value: 'paragraph', label: 'Normal' },
  { value: 'h1', label: 'Heading 1' },
  { value: 'h2', label: 'Heading 2' },
  { value: 'h3', label: 'Heading 3' },
  { value: 'h4', label: 'Heading 4' },
  { value: 'h5', label: 'Heading 5' },
  { value: 'h6', label: 'Heading 6' },
  { value: 'quote', label: 'Quote' },
  { value: 'code', label: 'Code Block' },
  { value: 'bullet', label: 'Bullet List' },
  { value: 'number', label: 'Numbered List' },
  { value: 'check', label: 'Check List' },
];

const BLOCK_TYPE_STYLES = {
  paragraph: { fontSize: 14, fontWeight: 400 },
  h1: { fontSize: 17, fontWeight: 700, lineHeight: 1.2 },
  h2: { fontSize: 15, fontWeight: 700, lineHeight: 1.2 },
  h3: { fontSize: 14, fontWeight: 600, lineHeight: 1.2 },
  h4: { fontSize: 13, fontWeight: 600 },
  h5: { fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' },
  h6: { fontSize: 10, fontWeight: 600, color: '#6b7280', letterSpacing: '0.04em', textTransform: 'uppercase' },
  quote: { fontSize: 14, fontStyle: 'italic', borderLeft: '3px solid #d1d5db', paddingLeft: 8, color: '#6c757d' },
  code: { fontSize: 11, fontFamily: 'Courier New, monospace', background: '#f3f4f6', padding: '1px 6px', borderRadius: 3, color: '#d63384' },
  bullet: { fontSize: 14 },
  number: { fontSize: 14 },
  check: { fontSize: 14 },
};

const FONT_FAMILIES_DATA = FONT_FAMILIES.map(f => ({ value: f, label: f }));

const PAGE_SIZES = {
  A4:     { width: 794,  height: 1123, label: 'A4 (210 × 297 mm · 8.27 × 11.69 in)' },
  A3:     { width: 1123, height: 1587, label: 'A3 (297 × 420 mm)' },
  A5:     { width: 559,  height: 794,  label: 'A5 (148 × 210 mm)' },
  Letter: { width: 816,  height: 1056, label: 'Letter (8.5 × 11 in)' },
  Legal:  { width: 816,  height: 1344, label: 'Legal (8.5 × 14 in)' },
  Stamp:  { width: 794,  height: 1304, label: 'Stamp (210 × 345 mm · 8.27 × 13.58 in)' },
};

const IN_TO_PX = 96;
const inToPx = (v) => v * IN_TO_PX;

const PAGE_TEMPLATES = [
  {
    id: 'stamp-01',
    label: 'Stamp 01',
    description: 'Stamp · T 4.1″ · B 1″ · L 1″ · R 1″',
    settings: { size: 'Stamp', marginTop: inToPx(4.1), marginBottom: inToPx(1), marginLeft: inToPx(1), marginRight: inToPx(1) },
  },
  {
    id: 'affidavit-legal',
    label: 'Affidavit (Legal)',
    description: 'Legal · T 1.5″ · B 1″ · L 1.25″ · R 1″',
    settings: { size: 'Legal', marginTop: inToPx(1.5), marginBottom: inToPx(1), marginLeft: inToPx(1.25), marginRight: inToPx(1) },
  },
  {
    id: 'application-a4',
    label: 'Application (A4)',
    description: 'A4 · 1″ all sides',
    settings: { size: 'A4', marginTop: inToPx(1), marginBottom: inToPx(1), marginLeft: inToPx(1), marginRight: inToPx(1) },
  },
  {
    id: 'letterhead-a4',
    label: 'Letterhead (A4)',
    description: 'A4 · T 2″ · B 1″ · L 1″ · R 1″',
    settings: { size: 'A4', marginTop: inToPx(2), marginBottom: inToPx(1), marginLeft: inToPx(1), marginRight: inToPx(1) },
  },
  {
    id: 'court-petition-legal',
    label: 'Court Petition (Legal)',
    description: 'Legal · T 4.1″ · B 0.8″ · L 1″ · R 1″',
    settings: { size: 'Legal', marginTop: inToPx(4.1), marginBottom: inToPx(0.8), marginLeft: inToPx(1), marginRight: inToPx(1) },
  },
];

const pageSettingsMatch = (a, b) =>
  a.size === b.size &&
  Math.abs(a.marginTop - b.marginTop) < 1 &&
  Math.abs(a.marginBottom - b.marginBottom) < 1 &&
  Math.abs(a.marginLeft - b.marginLeft) < 1 &&
  Math.abs(a.marginRight - b.marginRight) < 1;

const MARGIN_PRESETS = [
  { id: 'normal',    label: 'Normal',      desc: '1″ all sides',         settings: { marginTop: 96,  marginBottom: 96,  marginLeft: 96,  marginRight: 96  } },
  { id: 'narrow',    label: 'Narrow',      desc: '0.5″ all sides',       settings: { marginTop: 48,  marginBottom: 48,  marginLeft: 48,  marginRight: 48  } },
  { id: 'moderate',  label: 'Moderate',    desc: '1″ T/B · 0.75″ L/R',  settings: { marginTop: 96,  marginBottom: 96,  marginLeft: 72,  marginRight: 72  } },
  { id: 'wide',      label: 'Wide',        desc: '1″ T/B · 2″ L/R',     settings: { marginTop: 96,  marginBottom: 96,  marginLeft: 192, marginRight: 192 } },
  { id: 'office03',  label: 'Office 2003', desc: '1.25″ all sides',      settings: { marginTop: 120, marginBottom: 120, marginLeft: 120, marginRight: 120 } },
  { id: 'tight-tb',  label: 'Tight T/B',   desc: '0.5″ T/B · 1″ L/R',   settings: { marginTop: 48,  marginBottom: 48,  marginLeft: 96,  marginRight: 96  } },
];

const marginsMatch = (preset, current) =>
  Math.abs(preset.marginTop    - current.marginTop)    < 1 &&
  Math.abs(preset.marginBottom - current.marginBottom) < 1 &&
  Math.abs(preset.marginLeft   - current.marginLeft)   < 1 &&
  Math.abs(preset.marginRight  - current.marginRight)  < 1;

const noFocus = (e) => e.preventDefault();

// Apply an inline style to all text nodes within each of the given table cells.
// Must be called inside editor.update().
function applyStyleToTableCellNodes(cells, prop, val) {
  for (const cell of cells) {
    const textNodes = [];
    const collect = (node) => {
      if (node.getType() === 'text') textNodes.push(node);
      else node.getChildren?.().forEach(collect);
    };
    collect(cell);
    if (textNodes.length === 0) continue;
    const first = textNodes[0];
    const last  = textNodes[textNodes.length - 1];
    const sel = $createRangeSelection();
    sel.anchor.set(first.getKey(), 0, 'text');
    sel.focus.set(last.getKey(), last.getTextContentSize(), 'text');
    $patchStyleText(sel, { [prop]: val });
  }
}

function getSelectedNode(selection) {
  const anchor = selection.anchor;
  const anchorNode = anchor.getNode();
  return anchorNode;
}

function FontSizeInput({ value, onChange, editor }) {
  const [inputVal, setInputVal] = useState(value.replace(/pt$|px$/, ''));
  // Saved anchor/focus points captured on mousedown, before the editor loses focus.
  const savedSelRef = useRef(null);

  useEffect(() => {
    setInputVal(value.replace(/pt$|px$/, ''));
  }, [value]);

  const saveSelection = () => {
    if (!editor) return;
    editor.getEditorState().read(() => {
      const sel = $getSelection();
      if ($isTableSelection(sel)) {
        const cellKeys = sel.getNodes().filter($isTableCellNode).map(n => n.getKey());
        if (cellKeys.length) savedSelRef.current = { type: 'table', cellKeys };
      } else if ($isRangeSelection(sel)) {
        savedSelRef.current = {
          type: 'range',
          anchorKey: sel.anchor.key,
          anchorOffset: sel.anchor.offset,
          anchorType: sel.anchor.type,
          focusKey: sel.focus.key,
          focusOffset: sel.focus.offset,
          focusType: sel.focus.type,
        };
      }
    });
  };

  const applySize = (v) => {
    if (!editor) { onChange(v); return; }
    editor.update(() => {
      const saved = savedSelRef.current;
      let sel = $getSelection();

      if ($isTableSelection(sel)) {
        applyStyleToTableCellNodes(sel.getNodes().filter($isTableCellNode), 'font-size', v);
        return;
      }

      if (!$isRangeSelection(sel) && saved) {
        if (saved.type === 'table' && saved.cellKeys?.length) {
          const cells = saved.cellKeys.map(k => $getNodeByKey(k)).filter(Boolean).filter($isTableCellNode);
          if (cells.length) { applyStyleToTableCellNodes(cells, 'font-size', v); return; }
        }
        if (!saved.type || saved.type === 'range') {
          try {
            sel = $createRangeSelection();
            sel.anchor.set(saved.anchorKey, saved.anchorOffset, saved.anchorType);
            sel.focus.set(saved.focusKey, saved.focusOffset, saved.focusType);
            $setSelection(sel);
          } catch { sel = null; }
        }
      }

      if ($isRangeSelection(sel)) $patchStyleText(sel, { 'font-size': v });
    });
    onChange(v);
  };

  const commit = (raw) => {
    const n = parseInt(raw, 10);
    if (!isNaN(n) && n > 0 && n <= 400) applySize(`${n}px`);
  };

  const step = (dir) => {
    // Step buttons keep editor focused (noFocus), so current selection is valid.
    savedSelRef.current = null;
    const n = parseInt(inputVal, 10) || 14;
    commit(String(n + dir));
  };

  return (
    <div className="flex items-center border border-zinc-300 dark:border-zinc-600 rounded overflow-hidden">
      <button
        type="button"
        onMouseDown={noFocus}
        onClick={() => step(-1)}
        className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 border-r border-zinc-300 dark:border-zinc-600 transition-colors"
      >
        <IconChevronDown size={12} />
      </button>
      <input
        value={inputVal}
        onChange={e => setInputVal(e.target.value)}
        onMouseDown={saveSelection}
        onBlur={e => commit(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && commit(inputVal)}
        style={{
          width: 36, textAlign: 'center', border: 'none', outline: 'none',
          fontSize: 12, padding: '0 2px', lineHeight: '26px',
        }}
      />
      <button
        type="button"
        onMouseDown={noFocus}
        onClick={() => step(1)}
        className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 border-l border-zinc-300 dark:border-zinc-600 transition-colors"
      >
        <IconChevronUp size={12} />
      </button>
    </div>
  );
}

export default function ToolbarPlugin({ imageInputRef, onPageSettingsChange, pageSettings, showHeader, showFooter, onToggleHeader, onToggleFooter }) {
  const [editor] = useLexicalComposerContext();

  // Stores the last known RangeSelection so we can restore it when toolbar
  // dropdowns (font-family Select, color Popover, etc.) temporarily steal focus
  // and clear the Lexical selection — especially noticeable inside table cells.
  const savedSelRef = useRef(null);

  const [activeTab, setActiveTab] = useState('home');
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [isSubscript, setIsSubscript] = useState(false);
  const [isSuperscript, setIsSuperscript] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [blockType, setBlockType] = useState('paragraph');
  const [fontFamily, setFontFamily] = useState(DEFAULT_FONT_FAMILY);
  const [fontSize, setFontSize] = useState('14px');
  const [fontColor, setFontColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('transparent');
  const [elementFormat, setElementFormat] = useState('');
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [localPageSettings, setLocalPageSettings] = useState(
    pageSettings || { size: 'A4', marginTop: 96, marginBottom: 96, marginLeft: 72, marginRight: 72 }
  );
  const [marginUnit, setMarginUnit] = useState('inch');

  const activeTemplateId = useMemo(
    () => PAGE_TEMPLATES.find(t => pageSettingsMatch(t.settings, localPageSettings))?.id ?? null,
    [localPageSettings]
  );

  useEffect(() => {
    if (pageSettings) setLocalPageSettings(pageSettings);
  }, [pageSettings]);

  const [lineHeight, setLineHeight] = useState(DEFAULT_LINE_HEIGHT);
  const [ribbonCollapsed, setRibbonCollapsed] = useState(false);
  const [customSizeModalOpen, setCustomSizeModalOpen] = useState(false);
  const [customSizeWidthPx, setCustomSizeWidthPx] = useState(794);
  const [customSizeHeightPx, setCustomSizeHeightPx] = useState(1123);
  const [marginPresetModalOpen, setMarginPresetModalOpen] = useState(false);

  const applyLineHeight = useCallback((value) => {
    editor.update(() => {
      let selection = $getSelection();
      if (!$isRangeSelection(selection) && savedSelRef.current) {
        try {
          const s = savedSelRef.current;
          selection = $createRangeSelection();
          selection.anchor.set(s.anchorKey, s.anchorOffset, s.anchorType);
          selection.focus.set(s.focusKey,   s.focusOffset,  s.focusType);
          $setSelection(selection);
        } catch { selection = null; }
      }
      if (!$isRangeSelection(selection)) return;
      const blockKeys = new Set();
      const nodes = selection.getNodes();
      for (const n of nodes) {
        const top = n.getTopLevelElement?.();
        if (top) blockKeys.add(top.getKey());
      }
      if (blockKeys.size === 0) {
        const top = selection.anchor.getNode().getTopLevelElement?.();
        if (top) blockKeys.add(top.getKey());
      }
      for (const key of blockKeys) {
        const node = $getNodeByKey(key);
        if (!node || typeof node.setStyle !== 'function') continue;
        const current = node.getStyle?.() || '';
        const stripped = current.replace(/line-height\s*:\s*[^;]+;?\s*/gi, '').trim();
        const glue = stripped && !stripped.endsWith(';') ? '; ' : (stripped ? ' ' : '');
        node.setStyle(`${stripped}${glue}line-height: ${value};`);
      }
    });
  }, [editor]);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();

    if ($isTableSelection(selection)) {
      // Save selected cell keys so toolbar actions apply to all selected cells
      const cellKeys = selection.getNodes().filter($isTableCellNode).map(n => n.getKey());
      if (cellKeys.length) savedSelRef.current = { type: 'table', cellKeys };
      return;
    }

    if (!$isRangeSelection(selection)) return;

    // Keep a snapshot so applyStyle can restore it if focus was temporarily lost
    savedSelRef.current = {
      type:         'range',
      anchorKey:    selection.anchor.key,
      anchorOffset: selection.anchor.offset,
      anchorType:   selection.anchor.type,
      focusKey:     selection.focus.key,
      focusOffset:  selection.focus.offset,
      focusType:    selection.focus.type,
    };

    setIsBold(selection.hasFormat('bold'));
    setIsItalic(selection.hasFormat('italic'));
    setIsUnderline(selection.hasFormat('underline'));
    setIsStrikethrough(selection.hasFormat('strikethrough'));
    setIsCode(selection.hasFormat('code'));
    setIsSubscript(selection.hasFormat('subscript'));
    setIsSuperscript(selection.hasFormat('superscript'));

    const anchorNode = getSelectedNode(selection);
    const parent = anchorNode.getParent();
    setIsLink($isLinkNode(parent) || $isLinkNode(anchorNode));

    const element = anchorNode.getKey() === 'root'
      ? anchorNode
      : $findMatchingParent(anchorNode, e => {
          const p = e.getParent();
          return p !== null && $isRootOrShadowRoot(p);
        });

    if (element) {
      if ($isListNode(element)) {
        const parentList = $getNearestNodeOfType(anchorNode, ListNode);
        setBlockType(parentList ? parentList.getListType() : element.getListType());
      } else if ($isHeadingNode(element)) {
        setBlockType(element.getTag());
      } else if ($isCodeNode(element)) {
        setBlockType('code');
      } else {
        setBlockType(element.getType());
      }
    }

    if (element && typeof element.getFormatType === 'function') {
      setElementFormat(element.getFormatType());
    } else {
      setElementFormat('');
    }

    setFontFamily($getSelectionStyleValueForProperty(selection, 'font-family', DEFAULT_FONT_FAMILY));
    setFontSize($getSelectionStyleValueForProperty(selection, 'font-size', '14px'));
    setFontColor($getSelectionStyleValueForProperty(selection, 'color', '#000000') || '#000000');
    setBgColor($getSelectionStyleValueForProperty(selection, 'background-color', 'transparent') || 'transparent');

    // Sync line-height dropdown from the block element's style.
    if (element && typeof element.getStyle === 'function') {
      const blockStyle = element.getStyle() || '';
      const lhMatch = blockStyle.match(/line-height\s*:\s*([^;]+)/i);
      setLineHeight(lhMatch ? lhMatch[1].trim() : DEFAULT_LINE_HEIGHT);
    }
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(updateToolbar);
      }),
      editor.registerCommand(SELECTION_CHANGE_COMMAND, () => { updateToolbar(); return false; }, COMMAND_PRIORITY_CRITICAL),
      editor.registerCommand(CAN_UNDO_COMMAND, v => { setCanUndo(v); return false; }, COMMAND_PRIORITY_CRITICAL),
      editor.registerCommand(CAN_REDO_COMMAND, v => { setCanRedo(v); return false; }, COMMAND_PRIORITY_CRITICAL),
    );
  }, [editor, updateToolbar]);

  const applyBlockType = (type) => {
    editor.update(() => {
      let selection = $getSelection();
      if (!$isRangeSelection(selection) && savedSelRef.current) {
        const saved = savedSelRef.current;
        if (!saved.type || saved.type === 'range') {
          try {
            selection = $createRangeSelection();
            selection.anchor.set(saved.anchorKey, saved.anchorOffset, saved.anchorType);
            selection.focus.set(saved.focusKey, saved.focusOffset, saved.focusType);
            $setSelection(selection);
          } catch { selection = null; }
        }
      }
      if (!$isRangeSelection(selection)) return;
      if (type === 'paragraph') {
        $setBlocksType(selection, () => $createParagraphNode());
      } else if (type === 'quote') {
        $setBlocksType(selection, () => $createQuoteNode());
      } else if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(type)) {
        $setBlocksType(selection, () => $createHeadingNode(type));
      } else if (type === 'bullet') {
        editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
      } else if (type === 'number') {
        editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
      } else if (type === 'check') {
        editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
      } else if (type === 'code') {
        import('@lexical/code').then(({ $createCodeNode }) => {
          editor.update(() => {
            const sel = $getSelection();
            if ($isRangeSelection(sel)) $setBlocksType(sel, () => $createCodeNode());
          });
        });
      }
    });
  };

  const applyStyle = (prop, val) => {
    editor.update(() => {
      let selection = $getSelection();

      // Active multi-cell table selection — apply to each selected cell
      if ($isTableSelection(selection)) {
        applyStyleToTableCellNodes(selection.getNodes().filter($isTableCellNode), prop, val);
        return;
      }

      // If selection was lost (e.g. dropdown stole focus), restore from snapshot
      if (!$isRangeSelection(selection) && savedSelRef.current) {
        const saved = savedSelRef.current;
        if (saved.type === 'table' && saved.cellKeys?.length) {
          const cells = saved.cellKeys.map(k => $getNodeByKey(k)).filter(Boolean).filter($isTableCellNode);
          if (cells.length) { applyStyleToTableCellNodes(cells, prop, val); return; }
        }
        if (!saved.type || saved.type === 'range') {
          try {
            selection = $createRangeSelection();
            selection.anchor.set(saved.anchorKey, saved.anchorOffset, saved.anchorType);
            selection.focus.set(saved.focusKey,   saved.focusOffset,  saved.focusType);
            $setSelection(selection);
          } catch {
            selection = null;
          }
        }
      }

      if ($isRangeSelection(selection)) $patchStyleText(selection, { [prop]: val });
    });
  };

  const dispatch = (cmd, payload) => {
    editor.dispatchCommand(cmd, payload);
  };

  // Saves the current Lexical selection into savedSelRef before a toolbar
  // control (Select, Popover, etc.) steals focus from the editor.
  // Call this in onMouseDown or onOpenChange(open=true).
  const saveEditorSel = () => {
    editor.getEditorState().read(() => {
      const sel = $getSelection();
      if ($isTableSelection(sel)) {
        const cellKeys = sel.getNodes().filter($isTableCellNode).map(n => n.getKey());
        if (cellKeys.length) savedSelRef.current = { type: 'table', cellKeys };
      } else if ($isRangeSelection(sel)) {
        savedSelRef.current = {
          type: 'range',
          anchorKey: sel.anchor.key, anchorOffset: sel.anchor.offset, anchorType: sel.anchor.type,
          focusKey:  sel.focus.key,  focusOffset:  sel.focus.offset,  focusType:  sel.focus.type,
        };
      }
    });
  };

  // Toggle a text format (bold/italic/etc.) with selection restoration,
  // so clicking a format button works even if the editor briefly lost focus.
  const dispatchFormat = (format) => {
    editor.update(() => {
      let selection = $getSelection();
      if (!$isRangeSelection(selection) && savedSelRef.current) {
        const saved = savedSelRef.current;
        if (!saved.type || saved.type === 'range') {
          try {
            selection = $createRangeSelection();
            selection.anchor.set(saved.anchorKey, saved.anchorOffset, saved.anchorType);
            selection.focus.set(saved.focusKey, saved.focusOffset, saved.focusType);
            $setSelection(selection);
          } catch { selection = null; }
        }
      }
      if ($isRangeSelection(selection)) selection.formatText(format);
    });
  };

  // Apply alignment only to the paragraph at the cursor (collapsed) or all
  // explicitly selected paragraphs (range), so adjacent unselected lines are
  // never accidentally re-aligned.
  const applyAlignment = (align) => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      if (selection.isCollapsed()) {
        const anchorNode = selection.anchor.getNode();
        const topElement = $findMatchingParent(anchorNode, (e) => {
          const p = e.getParent();
          return p !== null && $isRootOrShadowRoot(p);
        });
        if (topElement && typeof topElement.setFormat === 'function') {
          topElement.setFormat(align);
        }
      } else {
        const nodes = selection.getNodes();
        const seen = new Set();
        for (const node of nodes) {
          const topEl = $findMatchingParent(node, (e) => {
            const p = e.getParent();
            return p !== null && $isRootOrShadowRoot(p);
          });
          if (topEl && !seen.has(topEl.getKey())) {
            seen.add(topEl.getKey());
            if (typeof topEl.setFormat === 'function') topEl.setFormat(align);
          }
        }
      }
    });
  };

  const clearFormatting = () => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      ['bold', 'italic', 'underline', 'strikethrough', 'code', 'subscript', 'superscript'].forEach(fmt => {
        if (selection.hasFormat(fmt)) dispatch(FORMAT_TEXT_COMMAND, fmt);
      });
      $patchStyleText(selection, { 'font-family': null, 'font-size': null, color: null, 'background-color': null });
    });
  };

  // Walk the whole document and repair any {{slug}} tokens that were split across
  // multiple TextNode fragments by partial formatting (bold/size/color on part of
  // the token). Merges each fragmented run back into one TextNode so the
  // PlaceholderPlugin transform fires and re-creates a clean PlaceholderNode.
  const syncPlaceholders = useCallback(() => {
    const PLACEHOLDER_RE = /\{\{[^{}]+\}\}/g;

    function processElement(element) {
      const children = element.getChildren();
      let run = [];

      const flushRun = () => {
        if (run.length < 2) { run = []; return; }

        const combined = run.map(n => n.getTextContent()).join('');
        if (!combined.includes('{{')) { run = []; return; }

        // Build character-range → run-index map
        let pos = 0;
        const ranges = run.map((n, i) => {
          const t = n.getTextContent();
          const start = pos;
          pos += t.length;
          return { i, start, end: pos };
        });

        const matches = [...combined.matchAll(PLACEHOLDER_RE)];
        let needsFix = false;
        for (const match of matches) {
          const mStart = match.index;
          const mEnd = match.index + match[0].length;
          const spanning = ranges.filter(r => r.end > mStart && r.start < mEnd);
          // Fragmented across multiple nodes, or a complete token sitting in a
          // plain TextNode (the transform should have caught it but didn't).
          if (
            spanning.length > 1 ||
            (spanning.length === 1 && run[spanning[0].i].getType() !== 'placeholder')
          ) {
            needsFix = true;
            break;
          }
        }

        if (needsFix) {
          const style = run[0].getStyle?.() || '';
          const merged = $createTextNode(combined);
          if (style) merged.setStyle(style);
          // Replace first node; the rest are still in the tree — remove them.
          run[0].replace(merged);
          for (let i = 1; i < run.length; i++) {
            if (run[i].getParent() !== null) run[i].remove();
          }
          // PlaceholderPlugin's registerNodeTransform fires after this update
          // and re-splits the merged TextNode into proper PlaceholderNode(s).
        }
        run = [];
      };

      for (const child of children) {
        const type = child.getType();
        if (type === 'text' || type === 'placeholder') {
          run.push(child);
        } else {
          flushRun();
          if (typeof child.getChildren === 'function') {
            processElement(child);
          }
        }
      }
      flushRun();
    }

    editor.update(() => {
      processElement($getRoot());
    });

    notifications.show({
      title: 'Placeholders synced',
      message: 'Fragmented tokens have been merged — save and preview to confirm.',
      color: 'teal',
      autoClose: 3000,
    });
  }, [editor]);

  const insertLink = () => {
    if (isLink) {
      dispatch(TOGGLE_LINK_COMMAND, null);
    } else {
      setLinkUrl('https://');
      setLinkModalOpen(true);
    }
  };

  const confirmLink = () => {
    if (linkUrl && linkUrl !== 'https://') {
      dispatch(TOGGLE_LINK_COMMAND, { url: linkUrl, target: '_blank' });
    }
    setLinkModalOpen(false);
    setLinkUrl('');
  };

  const insertTable = () => {
    import('@lexical/table').then(({ INSERT_TABLE_COMMAND }) => {
      dispatch(INSERT_TABLE_COMMAND, { rows: String(tableRows), columns: String(tableCols) });
    });
    setTableModalOpen(false);
  };

  const applyPageSettings = () => {
    if (onPageSettingsChange) onPageSettingsChange(localPageSettings);
  };

  const listType = blockType === 'bullet' ? 'bullet' : blockType === 'number' ? 'number' : blockType === 'check' ? 'check' : null;

  const iconBtn = (label, isActive, cmd, payload, Icon) => (
    <button
      title={label}
      onMouseDown={noFocus}
      onClick={() => dispatch(cmd, payload)}
      className={`p-1.5 rounded transition-colors text-zinc-700 dark:text-zinc-300 ${isActive ? 'bg-zinc-200 dark:bg-zinc-600' : 'hover:bg-zinc-100 dark:hover:bg-zinc-700'}`}
    >
      <Icon size={16} />
    </button>
  );

  const tabBtn = (id, label) => (
    <button
      key={id}
      className={`${styles.toolbarTabBtn} ${activeTab === id ? styles.toolbarTabBtnActive : ''}`}
      onMouseDown={noFocus}
      onClick={() => setActiveTab(id)}
    >
      {label}
    </button>
  );

  return (
    <>
      <div className={styles.toolbar}>

        {/* ── Tab row ── */}
        <div className={styles.toolbarTabs}>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            {tabBtn('home', 'Home')}
            {tabBtn('layout', 'Layout')}
            {tabBtn('settings', 'Settings')}
          </div>
          <button
            className={styles.collapseBtn}
            onMouseDown={noFocus}
            onClick={() => setRibbonCollapsed(v => !v)}
            title={ribbonCollapsed ? 'Expand ribbon' : 'Collapse ribbon'}
          >
            {ribbonCollapsed ? <IconChevronDown size={11} /> : <IconChevronUp size={11} />}
          </button>
        </div>

        {/* ── Home tab ── */}
        {!ribbonCollapsed && activeTab === 'home' && (
          <div className={styles.toolbarContent}>

            {/* History */}
            <div className={styles.toolbarGroup}>
              <button
                title="Undo"
                disabled={!canUndo}
                onMouseDown={noFocus}
                onClick={() => dispatch(UNDO_COMMAND, undefined)}
                className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 disabled:opacity-40 transition-colors"
              >
                <IconArrowBackUp size={16} />
              </button>
              <button
                title="Redo"
                disabled={!canRedo}
                onMouseDown={noFocus}
                onClick={() => dispatch(REDO_COMMAND, undefined)}
                className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 disabled:opacity-40 transition-colors"
              >
                <IconArrowForwardUp size={16} />
              </button>
            </div>

            {/* Style */}
            <div className={styles.toolbarGroup}>
              <Select value={blockType} onValueChange={applyBlockType}>
                <SelectTrigger
                  onMouseDown={saveEditorSel}
                  className="h-7 text-xs w-[135px] focus:ring-1 focus:ring-orange-500"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ zIndex: 9999 }}>
                  {BLOCK_TYPES.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Font */}
            <div className={styles.toolbarGroup}>
              <Select
                value={fontFamily}
                onValueChange={(v) => { setFontFamily(v); applyStyle('font-family', v); }}
              >
                <SelectTrigger
                  onMouseDown={saveEditorSel}
                  className="h-7 text-xs w-[155px] focus:ring-1 focus:ring-orange-500"
                  style={{ fontFamily }}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ zIndex: 9999 }} className="max-h-64">
                  {FONT_FAMILIES_DATA.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} className="text-xs" style={{ fontFamily: opt.value }}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex items-center gap-0">
                <FontSizeInput
                  value={fontSize}
                  onChange={v => setFontSize(v)}
                  editor={editor}
                />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      onMouseDown={saveEditorSel}
                      className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 transition-colors"
                    >
                      <IconChevronDown size={10} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent style={{ zIndex: 9999 }} className="w-36">
                    <div className="grid grid-cols-4 gap-0.5 p-1">
                      {COMMON_SIZES.map(s => (
                        <DropdownMenuItem
                          key={s}
                          onSelect={() => { const v = `${s}px`; setFontSize(v); applyStyle('font-size', v); }}
                          className={`px-1.5 py-0.5 text-xs rounded justify-center cursor-pointer ${fontSize === `${s}px` ? 'bg-orange-500 text-white focus:bg-orange-600 focus:text-white' : ''}`}
                        >
                          {s}
                        </DropdownMenuItem>
                      ))}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Format */}
            <div className={styles.toolbarGroup}>
              {iconBtn('Bold (Ctrl+B)', isBold, FORMAT_TEXT_COMMAND, 'bold', IconBold)}
              {iconBtn('Italic (Ctrl+I)', isItalic, FORMAT_TEXT_COMMAND, 'italic', IconItalic)}
              {iconBtn('Underline (Ctrl+U)', isUnderline, FORMAT_TEXT_COMMAND, 'underline', IconUnderline)}
              {iconBtn('Strikethrough', isStrikethrough, FORMAT_TEXT_COMMAND, 'strikethrough', IconStrikethrough)}
              {iconBtn('Inline code', isCode, FORMAT_TEXT_COMMAND, 'code', IconCode)}
              {iconBtn('Subscript', isSubscript, FORMAT_TEXT_COMMAND, 'subscript', IconSubscript)}
              {iconBtn('Superscript', isSuperscript, FORMAT_TEXT_COMMAND, 'superscript', IconSuperscript)}
              <button
                title="Clear formatting"
                onMouseDown={noFocus}
                onClick={clearFormatting}
                className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 transition-colors"
              >
                <IconClearFormatting size={16} />
              </button>
            </div>

            {/* Color */}
            <div className={styles.toolbarGroup}>
              <RadixPopover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    title="Text color"
                    onMouseDown={(e) => {
                      // Save selection before popover steals focus
                      editor.getEditorState().read(() => {
                        const sel = $getSelection();
                        if ($isTableSelection(sel)) {
                          const cellKeys = sel.getNodes().filter($isTableCellNode).map(n => n.getKey());
                          if (cellKeys.length) savedSelRef.current = { type: 'table', cellKeys };
                        } else if ($isRangeSelection(sel)) {
                          savedSelRef.current = {
                            type: 'range',
                            anchorKey: sel.anchor.key, anchorOffset: sel.anchor.offset, anchorType: sel.anchor.type,
                            focusKey:  sel.focus.key,  focusOffset:  sel.focus.offset,  focusType:  sel.focus.type,
                          };
                        }
                      });
                    }}
                    style={{
                      width: 22, height: 22, margin: 3, padding: 0, flexShrink: 0,
                      background: fontColor, border: '1px solid rgba(0,0,0,0.22)',
                      borderRadius: 3, cursor: 'pointer', display: 'block',
                    }}
                  />
                </PopoverTrigger>
                <PopoverContent className="w-auto p-3" style={{ zIndex: 9999 }}>
                  <div className="flex flex-col gap-2">
                    <input
                      type="color"
                      value={fontColor}
                      onChange={e => { setFontColor(e.target.value); applyStyle('color', e.target.value); }}
                      className="w-32 h-8 cursor-pointer border border-zinc-300 dark:border-zinc-600 rounded"
                    />
                    <div className="grid grid-cols-5 gap-1">
                      {['#000000', '#ffffff', '#e03131', '#2f9e44', '#1971c2', '#f08c00', '#7048e8', '#c2255c', '#6c757d', '#495057'].map(c => (
                        <button
                          key={c}
                          onMouseDown={noFocus}
                          onClick={() => { setFontColor(c); applyStyle('color', c); }}
                          style={{ background: c, width: 20, height: 20, borderRadius: 3, border: '1px solid rgba(0,0,0,0.2)', cursor: 'pointer' }}
                        />
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </RadixPopover>
            </div>

            {/* Alignment */}
            <div className={styles.toolbarGroup}>
              <button title="Align left" onMouseDown={noFocus} onClick={() => applyAlignment('left')} className={`p-1.5 rounded transition-colors text-zinc-700 dark:text-zinc-300 ${(elementFormat === 'left' || elementFormat === '' || elementFormat === 'start') ? 'bg-zinc-200 dark:bg-zinc-600' : 'hover:bg-zinc-100 dark:hover:bg-zinc-700'}`}><IconAlignLeft size={16} /></button>
              <button title="Center" onMouseDown={noFocus} onClick={() => applyAlignment('center')} className={`p-1.5 rounded transition-colors text-zinc-700 dark:text-zinc-300 ${elementFormat === 'center' ? 'bg-zinc-200 dark:bg-zinc-600' : 'hover:bg-zinc-100 dark:hover:bg-zinc-700'}`}><IconAlignCenter size={16} /></button>
              <button title="Align right" onMouseDown={noFocus} onClick={() => applyAlignment('right')} className={`p-1.5 rounded transition-colors text-zinc-700 dark:text-zinc-300 ${(elementFormat === 'right' || elementFormat === 'end') ? 'bg-zinc-200 dark:bg-zinc-600' : 'hover:bg-zinc-100 dark:hover:bg-zinc-700'}`}><IconAlignRight size={16} /></button>
              <button title="Justify" onMouseDown={noFocus} onClick={() => applyAlignment('justify')} className={`p-1.5 rounded transition-colors text-zinc-700 dark:text-zinc-300 ${elementFormat === 'justify' ? 'bg-zinc-200 dark:bg-zinc-600' : 'hover:bg-zinc-100 dark:hover:bg-zinc-700'}`}><IconAlignJustified size={16} /></button>
            </div>

            {/* Line height */}
            <div className={styles.toolbarGroup}>
              <div className="flex items-center gap-1">
                <IconLineHeight size={14} className="text-zinc-500 dark:text-zinc-400 flex-shrink-0" />
                <Select
                  value={lineHeight}
                  onValueChange={(v) => { setLineHeight(v); applyLineHeight(v); }}
                >
                  <SelectTrigger
                    onMouseDown={saveEditorSel}
                    className="h-7 text-xs w-[68px] focus:ring-1 focus:ring-orange-500 text-center"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ zIndex: 9999 }}>
                    {LINE_HEIGHTS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Lists */}
            <div className={styles.toolbarGroup}>
              <button
                title="Bullet list"
                onMouseDown={noFocus}
                onClick={() => listType === 'bullet' ? dispatch(REMOVE_LIST_COMMAND) : dispatch(INSERT_UNORDERED_LIST_COMMAND)}
                className={`p-1.5 rounded transition-colors text-zinc-700 dark:text-zinc-300 ${listType === 'bullet' ? 'bg-zinc-200 dark:bg-zinc-600' : 'hover:bg-zinc-100 dark:hover:bg-zinc-700'}`}
              >
                <IconList size={16} />
              </button>
              <button
                title="Numbered list"
                onMouseDown={noFocus}
                onClick={() => listType === 'number' ? dispatch(REMOVE_LIST_COMMAND) : dispatch(INSERT_ORDERED_LIST_COMMAND)}
                className={`p-1.5 rounded transition-colors text-zinc-700 dark:text-zinc-300 ${listType === 'number' ? 'bg-zinc-200 dark:bg-zinc-600' : 'hover:bg-zinc-100 dark:hover:bg-zinc-700'}`}
              >
                <IconListNumbers size={16} />
              </button>
              <button
                title="Check list"
                onMouseDown={noFocus}
                onClick={() => listType === 'check' ? dispatch(REMOVE_LIST_COMMAND) : dispatch(INSERT_CHECK_LIST_COMMAND)}
                className={`p-1.5 rounded transition-colors text-zinc-700 dark:text-zinc-300 ${listType === 'check' ? 'bg-zinc-200 dark:bg-zinc-600' : 'hover:bg-zinc-100 dark:hover:bg-zinc-700'}`}
              >
                <IconChecklist size={16} />
              </button>
            </div>

            {/* Page Break — prominent single-click action */}
            <button
              className={styles.pageBreakBtn}
              onMouseDown={noFocus}
              onClick={() => dispatch(INSERT_PAGE_BREAK_COMMAND, undefined)}
            >
              <IconPageBreak size={14} />
              Page Break
            </button>

            {/* Sync Placeholders — repairs tokens fragmented by partial formatting */}
            <button
              className={styles.pageBreakBtn}
              style={{ background: 'rgba(255, 153, 0, 0.12)', borderColor: '#FF9900', color: '#c97700' }}
              onMouseDown={noFocus}
              onClick={syncPlaceholders}
              title="Use this if filling a field only updates some occurrences in the document — repairs {{tokens}} that were split by formatting"
            >
              <IconBraces size={14} />
              Sync Placeholders
            </button>

          </div>
        )}

        {/* ── Layout tab ── */}
        {!ribbonCollapsed && activeTab === 'layout' && (
          <div className={styles.toolbarContent}>

            {/* Link */}
            <div className={styles.toolbarGroup}>
              <button
                title={isLink ? 'Remove link' : 'Insert link'}
                onMouseDown={noFocus}
                onClick={insertLink}
                className={`p-1.5 rounded transition-colors text-zinc-700 dark:text-zinc-300 ${isLink ? 'bg-zinc-200 dark:bg-zinc-600' : 'hover:bg-zinc-100 dark:hover:bg-zinc-700'}`}
              >
                <IconLink size={16} />
              </button>
            </div>

            {/* Highlight */}
            <div className={styles.toolbarGroup}>
              <RadixPopover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    title="Highlight color"
                    className="flex flex-col items-center gap-0.5 p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 transition-colors"
                  >
                    <IconHighlight size={13} />
                    <div style={{
                      width: 14, height: 3,
                      background: bgColor === 'transparent' ? '#d1d5db' : bgColor,
                      borderRadius: 1, border: '1px solid rgba(0,0,0,0.15)'
                    }} />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-3" style={{ zIndex: 9999 }}>
                  <div className="flex flex-col gap-2">
                    <input
                      type="color"
                      value={bgColor === 'transparent' ? '#ffffff' : bgColor}
                      onChange={e => { setBgColor(e.target.value); applyStyle('background-color', e.target.value); }}
                      className="w-32 h-8 cursor-pointer border border-zinc-300 dark:border-zinc-600 rounded"
                    />
                    <div className="grid grid-cols-4 gap-1">
                      {['#fff9c4', '#c8e6c9', '#bbdefb', '#f8bbd0', '#d1d5db', '#e1bee7', '#ffccbc', '#b2dfdb'].map(c => (
                        <button
                          key={c}
                          onMouseDown={noFocus}
                          onClick={() => { setBgColor(c); applyStyle('background-color', c); }}
                          style={{ background: c, width: 20, height: 20, borderRadius: 3, border: '1px solid rgba(0,0,0,0.2)', cursor: 'pointer' }}
                        />
                      ))}
                    </div>
                    <button
                      onMouseDown={noFocus}
                      onClick={() => { setBgColor('transparent'); applyStyle('background-color', null); }}
                      className="text-xs text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 underline text-left"
                    >
                      Clear highlight
                    </button>
                  </div>
                </PopoverContent>
              </RadixPopover>
            </div>

            {/* Indent */}
            <div className={styles.toolbarGroup}>
              <button title="Decrease indent" onMouseDown={noFocus} onClick={() => dispatch(OUTDENT_CONTENT_COMMAND, undefined)} className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 transition-colors"><IconIndentDecrease size={16} /></button>
              <button title="Increase indent" onMouseDown={noFocus} onClick={() => dispatch(INDENT_CONTENT_COMMAND, undefined)} className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 transition-colors"><IconIndentIncrease size={16} /></button>
            </div>

            {/* Header / Footer toggles */}
            <div className={styles.toolbarGroup}>
              <button
                title={showHeader ? 'Hide header' : 'Show header'}
                onMouseDown={noFocus}
                onClick={onToggleHeader}
                className={`p-1.5 rounded transition-colors text-zinc-700 dark:text-zinc-300 ${showHeader ? 'bg-zinc-200 dark:bg-zinc-600' : 'hover:bg-zinc-100 dark:hover:bg-zinc-700'}`}
              >
                <span style={{ fontSize: 11, fontWeight: 700, lineHeight: 1 }}>H</span>
              </button>
              <button
                title={showFooter ? 'Hide footer' : 'Show footer'}
                onMouseDown={noFocus}
                onClick={onToggleFooter}
                className={`p-1.5 rounded transition-colors text-zinc-700 dark:text-zinc-300 ${showFooter ? 'bg-zinc-200 dark:bg-zinc-600' : 'hover:bg-zinc-100 dark:hover:bg-zinc-700'}`}
              >
                <span style={{ fontSize: 11, fontWeight: 700, lineHeight: 1 }}>F</span>
              </button>
            </div>

            {/* Insert — Table & Image direct, rest in menu */}
            <div className={styles.toolbarGroup}>
              <button title="Insert table" onMouseDown={noFocus} onClick={() => setTableModalOpen(true)} className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 transition-colors">
                <IconTable size={16} />
              </button>
              {imageInputRef && (
                <button title="Insert image" onMouseDown={noFocus} onClick={() => imageInputRef.current?.click()} className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 transition-colors">
                  <IconPhoto size={16} />
                </button>
              )}
              <button title="Horizontal rule" onMouseDown={noFocus} onClick={() => dispatch(INSERT_HORIZONTAL_RULE_COMMAND, undefined)} className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 transition-colors">
                <IconMinus size={16} />
              </button>

              {/* Columns layout */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button title="Columns layout" onMouseDown={noFocus} className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 transition-colors">
                    <IconLayoutColumns size={16} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent style={{ zIndex: 9999, width: 200 }}>
                  <div className="px-2 py-1 text-xs text-zinc-500 dark:text-zinc-400 font-medium">Insert columns layout</div>
                  <DropdownMenuSeparator />
                  {[
                    { label: '2 equal columns',      templateColumns: '1fr 1fr',         count: 2 },
                    { label: '3 equal columns',      templateColumns: '1fr 1fr 1fr',     count: 3 },
                    { label: '4 equal columns',      templateColumns: '1fr 1fr 1fr 1fr', count: 4 },
                    { label: 'Left narrow (1:2)',     templateColumns: '1fr 2fr',         count: 2 },
                    { label: 'Right narrow (2:1)',    templateColumns: '2fr 1fr',         count: 2 },
                    { label: 'Left sidebar (1:3)',    templateColumns: '1fr 3fr',         count: 2 },
                    { label: 'Right sidebar (3:1)',   templateColumns: '3fr 1fr',         count: 2 },
                  ].map(({ label, templateColumns, count }) => (
                    <DropdownMenuItem
                      key={label}
                      onClick={() => dispatch(INSERT_LAYOUT_COMMAND, { templateColumns, columnCount: count })}
                    >
                      <IconLayoutColumns size={14} className="mr-2 flex-shrink-0" />
                      <div>
                        <span className="text-xs font-medium block">{label}</span>
                        <span className="text-xs text-zinc-500 dark:text-zinc-400 font-mono">{templateColumns}</span>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Visual separator */}
            <div className="w-px h-6 bg-amber-200 dark:bg-amber-800 mx-1 flex-shrink-0" />

            {/* Page size */}
            <div className={styles.toolbarGroup}>
              <IconRuler size={14} style={{ color: '#a07030', flexShrink: 0 }} title="Page size" />
              <Select
                value={localPageSettings.size}
                onValueChange={(v) => {
                  if (!v) return;
                  if (v === 'Custom') {
                    const curW = localPageSettings.size === 'Custom'
                      ? (localPageSettings.customWidth ?? 794)
                      : (PAGE_SIZES[localPageSettings.size]?.width ?? 794);
                    const curH = localPageSettings.size === 'Custom'
                      ? (localPageSettings.customHeight ?? 1123)
                      : (PAGE_SIZES[localPageSettings.size]?.height ?? 1123);
                    setCustomSizeWidthPx(curW);
                    setCustomSizeHeightPx(curH);
                    setCustomSizeModalOpen(true);
                    return;
                  }
                  const next = { ...localPageSettings, size: v };
                  setLocalPageSettings(next);
                  onPageSettingsChange?.(next);
                }}
              >
                <SelectTrigger className="h-7 text-xs w-[105px] focus:ring-1 focus:ring-orange-500">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ zIndex: 9999 }}>
                  {Object.keys(PAGE_SIZES).map(k => (
                    <SelectItem key={k} value={k} className="text-xs">{k}</SelectItem>
                  ))}
                  <SelectItem value="Custom" className="text-xs">Custom…</SelectItem>
                </SelectContent>
              </Select>
              {localPageSettings.size === 'Custom' && (
                <button
                  title="Edit custom size"
                  onMouseDown={noFocus}
                  onClick={() => {
                    setCustomSizeWidthPx(localPageSettings.customWidth ?? 794);
                    setCustomSizeHeightPx(localPageSettings.customHeight ?? 1123);
                    setCustomSizeModalOpen(true);
                  }}
                  className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 transition-colors"
                >
                  <IconPencil size={13} />
                </button>
              )}
            </div>

            {/* Margin presets + custom inputs */}
            <div className={styles.toolbarGroup}>
              {/* Preset picker */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button title="Margin presets" onMouseDown={noFocus} className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 transition-colors">
                    <IconBorderOuter size={16} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent style={{ zIndex: 9999, width: 220 }}>
                  {MARGIN_PRESETS.map(preset => (
                    <DropdownMenuItem
                      key={preset.id}
                      onClick={() => {
                        const next = { ...localPageSettings, ...preset.settings };
                        setLocalPageSettings(next);
                        onPageSettingsChange?.(next);
                      }}
                    >
                      <div className="flex items-center justify-between w-full">
                        <div>
                          <span className="text-xs font-semibold block">{preset.label}</span>
                          <span className="text-xs text-zinc-500 dark:text-zinc-400">{preset.desc}</span>
                        </div>
                        {marginsMatch(preset.settings, localPageSettings) && (
                          <IconCheck size={12} style={{ color: '#FF9900', flexShrink: 0 }} />
                        )}
                      </div>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setMarginPresetModalOpen(true)}>
                    <IconSettings size={12} className="mr-2" />
                    Custom margins…
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Unit selector */}
              <div className="flex rounded border border-zinc-300 dark:border-zinc-600 overflow-hidden text-xs">
                {[{ value: 'px', label: 'px' }, { value: 'mm', label: 'mm' }, { value: 'inch', label: 'in' }].map(opt => (
                  <button
                    key={opt.value}
                    onMouseDown={noFocus}
                    onClick={() => setMarginUnit(opt.value)}
                    className={`px-2 py-1 transition-colors ${marginUnit === opt.value ? 'bg-orange-500 text-white' : 'bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-700'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Inline custom margin inputs */}
              {(() => {
                const unit = getUnit(marginUnit);
                const toDisplay = (px) => roundTo(unit.fromPx(Number(px) || 0), unit.decimals);
                const setMargin = (key, displayValue) => {
                  const v = typeof displayValue === 'number' ? displayValue : parseFloat(displayValue);
                  if (Number.isNaN(v)) return;
                  setLocalPageSettings(s => ({ ...s, [key]: unit.toPx(v) }));
                };
                return (
                  <>
                    <input type="number" title="Top margin" value={toDisplay(localPageSettings.marginTop)} onChange={e => setMargin('marginTop', Number(e.target.value))} min={0} step={unit.step} placeholder="Top" className="w-16 text-xs px-1.5 py-1 rounded border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 focus:outline-none focus:ring-1 focus:ring-orange-500 h-7" />
                    <input type="number" title="Bottom margin" value={toDisplay(localPageSettings.marginBottom)} onChange={e => setMargin('marginBottom', Number(e.target.value))} min={0} step={unit.step} placeholder="Bottom" className="w-16 text-xs px-1.5 py-1 rounded border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 focus:outline-none focus:ring-1 focus:ring-orange-500 h-7" />
                    <input type="number" title="Left margin" value={toDisplay(localPageSettings.marginLeft)} onChange={e => setMargin('marginLeft', Number(e.target.value))} min={0} step={unit.step} placeholder="Left" className="w-16 text-xs px-1.5 py-1 rounded border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 focus:outline-none focus:ring-1 focus:ring-orange-500 h-7" />
                    <input type="number" title="Right margin" value={toDisplay(localPageSettings.marginRight)} onChange={e => setMargin('marginRight', Number(e.target.value))} min={0} step={unit.step} placeholder="Right" className="w-16 text-xs px-1.5 py-1 rounded border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 focus:outline-none focus:ring-1 focus:ring-orange-500 h-7" />
                    <button onMouseDown={noFocus} onClick={applyPageSettings} className="px-2 py-1 text-xs rounded bg-orange-500 hover:bg-orange-600 text-white transition-colors h-7">Apply</button>
                  </>
                );
              })()}
            </div>

            {/* Divider */}
            <div className="w-px h-6 bg-amber-200 dark:bg-amber-800 mx-1 flex-shrink-0" />

            {/* Clear Formatting */}
            <div className={styles.toolbarGroup}>
              <button title="Clear formatting on selected text" onMouseDown={noFocus} onClick={clearFormatting} className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 transition-colors">
                <IconClearFormatting size={16} />
              </button>
            </div>

          </div>
        )}

        {/* ── Settings tab — custom document templates ── */}
        {!ribbonCollapsed && activeTab === 'settings' && (
          <div className={styles.settingsPanel}>
            {PAGE_TEMPLATES.map(tpl => (
              <button
                key={tpl.id}
                className={`${styles.templateCard} ${activeTemplateId === tpl.id ? styles.templateCardActive : ''}`}
                onMouseDown={noFocus}
                onClick={() => {
                  const next = { ...localPageSettings, ...tpl.settings };
                  setLocalPageSettings(next);
                  onPageSettingsChange?.(next);
                }}
              >
                {activeTemplateId === tpl.id && (
                  <span className={styles.templateCardCheck}>✓</span>
                )}
                <span className={styles.templateCardLabel}>{tpl.label}</span>
                <span className={styles.templateCardDesc}>{tpl.description}</span>
              </button>
            ))}
          </div>
        )}

      </div>

      {/* ── Link modal ── */}
      <Dialog open={linkModalOpen} onOpenChange={setLinkModalOpen}>
        <DialogContent className="max-w-sm" style={{ zIndex: 9999 }}>
          <DialogHeader>
            <DialogTitle>Insert Link</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3 mt-2">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-zinc-700 dark:text-zinc-300">URL</label>
              <input
                type="text"
                value={linkUrl}
                onChange={e => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
                onKeyDown={e => e.key === 'Enter' && confirmLink()}
                autoFocus
                className="w-full px-3 py-2 rounded-md border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <button onClick={confirmLink} className="px-4 py-2 text-sm rounded bg-orange-500 hover:bg-orange-600 text-white transition-colors font-medium">
              Insert
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Table modal ── */}
      <Dialog open={tableModalOpen} onOpenChange={setTableModalOpen}>
        <DialogContent className="max-w-xs" style={{ zIndex: 9999 }}>
          <DialogHeader>
            <DialogTitle>Insert Table</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3 mt-2">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Rows</label>
              <input
                type="number"
                value={tableRows}
                onChange={e => setTableRows(Number(e.target.value))}
                min={1}
                max={20}
                className="w-full px-3 py-2 rounded-md border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Columns</label>
              <input
                type="number"
                value={tableCols}
                onChange={e => setTableCols(Number(e.target.value))}
                min={1}
                max={10}
                className="w-full px-3 py-2 rounded-md border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <button onClick={insertTable} className="px-4 py-2 text-sm rounded bg-orange-500 hover:bg-orange-600 text-white transition-colors font-medium">
              Insert
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Custom page size modal ── */}
      <Dialog open={customSizeModalOpen} onOpenChange={setCustomSizeModalOpen}>
        <DialogContent className="max-w-xs" style={{ zIndex: 9999 }}>
          <DialogHeader>
            <DialogTitle>Custom Page Size</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3 mt-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-500 dark:text-zinc-400">Unit</span>
              <div className="flex rounded border border-zinc-300 dark:border-zinc-600 overflow-hidden text-xs">
                {[{ value: 'px', label: 'px' }, { value: 'mm', label: 'mm' }, { value: 'cm', label: 'cm' }, { value: 'inch', label: 'in' }].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setMarginUnit(opt.value)}
                    className={`px-2 py-1 transition-colors ${marginUnit === opt.value ? 'bg-orange-500 text-white' : 'bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-700'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            {(() => {
              const unit = getUnit(marginUnit);
              return (
                <div className="flex items-center gap-3">
                  <div className="flex flex-col gap-1 flex-1">
                    <label className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Width ({unit.label})</label>
                    <input
                      type="number"
                      value={roundTo(unit.fromPx(customSizeWidthPx), unit.decimals)}
                      onChange={e => setCustomSizeWidthPx(unit.toPx(Number(e.target.value) || 0))}
                      min={roundTo(unit.fromPx(100), unit.decimals)}
                      step={unit.step}
                      className="w-full px-3 py-2 rounded-md border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div className="flex flex-col gap-1 flex-1">
                    <label className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Height ({unit.label})</label>
                    <input
                      type="number"
                      value={roundTo(unit.fromPx(customSizeHeightPx), unit.decimals)}
                      onChange={e => setCustomSizeHeightPx(unit.toPx(Number(e.target.value) || 0))}
                      min={roundTo(unit.fromPx(100), unit.decimals)}
                      step={unit.step}
                      className="w-full px-3 py-2 rounded-md border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                </div>
              );
            })()}
            <button
              onClick={() => {
                const next = {
                  ...localPageSettings,
                  size: 'Custom',
                  customWidth: customSizeWidthPx,
                  customHeight: customSizeHeightPx,
                };
                setLocalPageSettings(next);
                onPageSettingsChange?.(next);
                setCustomSizeModalOpen(false);
              }}
              className="px-4 py-2 text-sm rounded bg-orange-500 hover:bg-orange-600 text-white transition-colors font-medium"
            >
              Apply
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Custom margins modal (from preset menu) ── */}
      <Dialog open={marginPresetModalOpen} onOpenChange={setMarginPresetModalOpen}>
        <DialogContent className="max-w-xs" style={{ zIndex: 9999 }}>
          <DialogHeader>
            <DialogTitle>Custom Margins</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3 mt-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-500 dark:text-zinc-400">Unit</span>
              <div className="flex rounded border border-zinc-300 dark:border-zinc-600 overflow-hidden text-xs">
                {[{ value: 'px', label: 'px' }, { value: 'mm', label: 'mm' }, { value: 'cm', label: 'cm' }, { value: 'inch', label: 'in' }].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setMarginUnit(opt.value)}
                    className={`px-2 py-1 transition-colors ${marginUnit === opt.value ? 'bg-orange-500 text-white' : 'bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-700'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            {(() => {
              const unit = getUnit(marginUnit);
              const toDisplay = (px) => roundTo(unit.fromPx(Number(px) || 0), unit.decimals);
              const setMargin = (key, displayValue) => {
                const v = typeof displayValue === 'number' ? displayValue : parseFloat(displayValue);
                if (Number.isNaN(v)) return;
                setLocalPageSettings(s => ({ ...s, [key]: unit.toPx(v) }));
              };
              return (
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Top ({unit.label})</label>
                    <input type="number" value={toDisplay(localPageSettings.marginTop)} onChange={e => setMargin('marginTop', Number(e.target.value))} min={0} step={unit.step} className="w-full px-3 py-2 rounded-md border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Bottom ({unit.label})</label>
                    <input type="number" value={toDisplay(localPageSettings.marginBottom)} onChange={e => setMargin('marginBottom', Number(e.target.value))} min={0} step={unit.step} className="w-full px-3 py-2 rounded-md border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Left ({unit.label})</label>
                    <input type="number" value={toDisplay(localPageSettings.marginLeft)} onChange={e => setMargin('marginLeft', Number(e.target.value))} min={0} step={unit.step} className="w-full px-3 py-2 rounded-md border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Right ({unit.label})</label>
                    <input type="number" value={toDisplay(localPageSettings.marginRight)} onChange={e => setMargin('marginRight', Number(e.target.value))} min={0} step={unit.step} className="w-full px-3 py-2 rounded-md border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
                  </div>
                </div>
              );
            })()}
            <button
              onClick={() => {
                onPageSettingsChange?.(localPageSettings);
                setMarginPresetModalOpen(false);
              }}
              className="px-4 py-2 text-sm rounded bg-orange-500 hover:bg-orange-600 text-white transition-colors font-medium"
            >
              Apply
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
